import { Link, useNavigate } from '@tanstack/react-router';
import { Menu as MenuIcon, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { Menu } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { useStore } from '@/shared/store/store-context';

import { DeleteMenuDialog } from '../components/delete-menu-dialog';
import { MenuDialog } from '../components/menu-dialog';
import { useMenus } from '../hooks/use-menus';
import { countNodes, fromMenuItems } from '../schemas/menu-tree';

function Menus() {
  const { t } = useTranslation('menus');
  const store = useStore();
  const format = useFormat();
  const navigate = useNavigate();
  const query = useMenus(store.id);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Menu | null>(null);
  const menus = query.data ?? [];

  const addButton = (tour?: string) => (
    <Can permission="content.write">
      <Button onClick={() => setCreating(true)} data-tour={tour}>
        <Plus aria-hidden />
        {t('create.action')}
      </Button>
    </Can>
  );

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        actions={addButton('content.menus.new')}
      />
      {query.isPending ? (
        <Skeleton className="h-48" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : menus.length === 0 ? (
        <EmptyState
          icon={MenuIcon}
          title={t('empty.title')}
          description={t('empty.description')}
          action={addButton()}
          tour="content.menus.empty"
          tourLink
        />
      ) : (
        <Card className="gap-0 overflow-hidden py-0" data-tour="content.menus.table">
          <Table aria-label={t('title')}>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-5">{t('columns.menu')}</TableHead>
                <TableHead>{t('columns.handle')}</TableHead>
                <TableHead className="text-right">{t('columns.items')}</TableHead>
                <TableHead>{t('columns.updated')}</TableHead>
                <TableHead className="pr-5">
                  <span className="sr-only">{t('columns.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {menus.map((menu) => (
                <TableRow key={menu.id}>
                  <TableCell className="pl-5">
                    <Link
                      to="/s/$storeId/content/menus/$menuId"
                      params={{ storeId: store.id, menuId: menu.id }}
                      className="font-medium hover:underline"
                    >
                      {menu.title}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-[13px]">{menu.handle}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {countNodes(fromMenuItems(menu.items))}
                  </TableCell>
                  <TableCell>
                    <span title={format.utc(menu.updatedAt)}>
                      {format.relative(menu.updatedAt)}
                    </span>
                  </TableCell>
                  <TableCell className="pr-5 text-right">
                    <Can permission="content.write">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('actions.deleteMenu', { title: menu.title })}
                        onClick={() => setDeleting(menu)}
                      >
                        <Trash2 aria-hidden />
                      </Button>
                    </Can>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      {creating ? (
        <MenuDialog
          storeId={store.id}
          menu={null}
          open
          onOpenChange={setCreating}
          onCreated={(menu) =>
            void navigate({
              to: '/s/$storeId/content/menus/$menuId',
              params: { storeId: store.id, menuId: menu.id },
            })
          }
        />
      ) : null}
      <DeleteMenuDialog
        storeId={store.id}
        menu={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function MenusPage() {
  return (
    <FeatureErrorBoundary name="menus.list">
      <Menus />
    </FeatureErrorBoundary>
  );
}
